import { Component, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { UserModel } from '../../core/models/user.model';
import { PermissionType } from '../../core/enum/permission-type.enum';

@Component({
    selector: 'app-admin-layout',
    standalone: false,
    templateUrl: './admin-layout.component.html',
    styleUrls: ['./admin-layout.component.scss']
})
export class AdminLayoutComponent implements OnInit {
    menuItems: MenuItem[] = [];
    breadcrumbs: MenuItem[] = [];
    userMenu: MenuItem[] = [];
    sidebarVisible: boolean = true;
    currentUser: UserModel | null = null;

    constructor(
        private authService: AuthService,
        private router: Router,
        private activatedRoute: ActivatedRoute
    ) { }

    ngOnInit(): void {
        this.authService.token$.subscribe(token => {
            this.currentUser = token?.profile || null;
            this.buildMenu();
            this.buildUserMenu();
        });

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
        });

        this.breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
    }

    private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
        const children: ActivatedRoute[] = route.children;

        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
            if (routeURL !== '') {
                url += `/${routeURL}`;
            }

            let label = child.snapshot.data['breadcrumb'];

            // If no explicit breadcrumb data, try to derive it
            if (!label) {
                // Check if this route is a parameter route (e.g., :id)
                if (child.routeConfig && child.routeConfig.path && child.routeConfig.path.startsWith(':')) {
                    // Use the singular form of the previous breadcrumb if available
                    const parentLabel = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : '';
                    if (parentLabel) {
                        // Simple singularization: remove trailing 's'
                        label = parentLabel.endsWith('s') ? parentLabel.slice(0, -1) : parentLabel;
                        // Special case checking or more advanced singularization could go here
                        if (parentLabel === 'Categories') label = 'Category';
                        if (parentLabel === 'Inventories') label = 'Inventory';
                    } else {
                        label = 'Details'; // Fallback
                    }
                } else {
                    label = this.getLabelFromUrl(routeURL);
                }
            }

            if (label) {
                breadcrumbs.push({ label, routerLink: url });
            }

            return this.createBreadcrumbs(child, url, breadcrumbs);
        }

        return breadcrumbs;
    }

    private getLabelFromUrl(url: string): string {
        // Fallback: capitalize first letter of each word in URL segment
        return url.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }

    private hasPermission(perm: string): boolean {
        const permissions = this.currentUser?.permissions || [];
        return permissions.some(p => p.name === perm);
    }

    private buildMenu(): void {
        const p = PermissionType;
        const has = (perm: string) => this.hasPermission(perm);

        this.menuItems = [
            {
                label: 'Dashboard',
                icon: 'pi pi-home',
                routerLink: '/dashboard',
                visible: true
            },
            {
                label: 'Users & Access',
                icon: 'pi pi-users',
                visible: has(p.INDEX_USER) || has(p.INDEX_ROLE),
                expanded: true,
                items: [
                    {
                        label: 'Users',
                        icon: 'pi pi-user',
                        routerLink: '/users',
                        visible: has(p.INDEX_USER)
                    },
                    {
                        label: 'Roles',
                        icon: 'pi pi-shield',
                        routerLink: '/roles',
                        visible: has(p.INDEX_ROLE)
                    },
                    {
                        label: 'Permissions',
                        icon: 'pi pi-key',
                        routerLink: '/permissions',
                        visible: has(p.INDEX_ROLE)
                    }
                ]
            },
            {
                label: 'Companies',
                icon: 'pi pi-building',
                routerLink: '/companies',
                visible: true
            },
            {
                label: 'AI Management',
                icon: 'pi pi-comments',
                routerLink: '/ai',
                visible: has(p.MANAGE_AI_FEATURES) || has(p.VIEW_AI_USAGE) || has(p.MANAGE_AI_KEYS)
            },
            {
                label: 'Patients',
                icon: 'pi pi-id-card',
                routerLink: '/patients',
                visible: has(p.INDEX_PATIENT)
            },
            {
                label: 'Bookings',
                icon: 'pi pi-calendar',
                routerLink: '/bookings',
                visible: has(p.INDEX_APPOINTMENT)
            },
            {
                label: 'Timeslots',
                icon: 'pi pi-clock',
                routerLink: '/timeslots',
                visible: has(p.INDEX_APPOINTMENT)
            },
            {
                label: 'Services',
                icon: 'pi pi-briefcase',
                visible: has(p.INDEX_CATEGORY) || has(p.INDEX_SERVICE) || has(p.INDEX_SUBSCRIPTION) || has(p.INDEX_SERVICE_ZONE),
                items: [
                    {
                        label: 'Categories',
                        icon: 'pi pi-list',
                        routerLink: '/categories',
                        visible: has(p.INDEX_CATEGORY)
                    },
                    {
                        label: 'Service Zones',
                        icon: 'pi pi-map-marker',
                        routerLink: '/service-zones',
                        visible: has(p.INDEX_SERVICE_ZONE)
                    },
                    {
                        label: 'Service List',
                        icon: 'pi pi-book',
                        routerLink: '/services',
                        routerLinkActiveOptions: { exact: true },
                        visible: has(p.INDEX_SERVICE)
                    },
                    {
                        label: 'IV Drip Types',
                        icon: 'pi pi-filter',
                        routerLink: '/services/iv-drip-types',
                        visible: has(p.INDEX_SERVICE)
                    },
                    {
                        label: 'Subscriptions',
                        icon: 'pi pi-star',
                        routerLink: '/subscriptions',
                        visible: has(p.INDEX_SUBSCRIPTION)
                    }
                ]
            },
            {
                label: 'Finance',
                icon: 'pi pi-money-bill',
                visible: has(p.INDEX_INVOICE) || has(p.INDEX_ANALYTICS),
                items: [
                    {
                        label: 'Invoices',
                        icon: 'pi pi-file',
                        routerLink: '/finance/invoices',
                        visible: has(p.INDEX_INVOICE)
                    },
                    {
                        label: 'Analytics & Reports',
                        icon: 'pi pi-chart-line',
                        routerLink: '/analytics',
                        visible: has(p.INDEX_ANALYTICS)
                    }
                ]
            },
            {
                label: 'Content',
                icon: 'pi pi-file-edit',
                visible: has(p.INDEX_STATIC_CONTENT) || has(p.INDEX_BANNER) || has(p.INDEX_HOME_TILE),
                items: [
                    {
                        label: 'Static Pages',
                        icon: 'pi pi-file',
                        routerLink: '/content/static',
                        visible: has(p.INDEX_STATIC_CONTENT)
                    },
                    {
                        label: 'Banners',
                        icon: 'pi pi-images',
                        routerLink: '/content/banners',
                        visible: has(p.INDEX_BANNER)
                    },
                    {
                        label: 'Home Tiles',
                        icon: 'pi pi-th-large',
                        routerLink: '/content/tiles',
                        visible: has(p.INDEX_HOME_TILE)
                    }
                ]
            },
            {
                label: 'Support',
                icon: 'pi pi-info-circle',
                visible: has(p.INDEX_CONVERSATION) || has(p.INDEX_SUPPORT_TICKET),
                items: [
                    {
                        label: 'Conversations',
                        icon: 'pi pi-comments',
                        routerLink: '/support/conversations',
                        visible: has(p.INDEX_CONVERSATION)
                    },
                    {
                        label: 'Tickets',
                        icon: 'pi pi-ticket',
                        routerLink: '/support/tickets',
                        visible: has(p.INDEX_SUPPORT_TICKET)
                    }
                ]
            },
            {
                label: 'Security',
                icon: 'pi pi-lock',
                visible: has(p.INDEX_CREDENTIAL_CHANGE_RECORD),
                items: [
                    {
                        label: 'Credential Changes',
                        icon: 'pi pi-key',
                        routerLink: '/security/credential-change-records',
                        visible: has(p.INDEX_CREDENTIAL_CHANGE_RECORD)
                    }
                ]
            }
        ];
    }

    private buildUserMenu(): void {
        this.userMenu = [
            {
                label: 'Profile',
                icon: 'pi pi-user',
                command: () => this.router.navigate(['/profile'])
            },
            {
                label: 'Logout',
                icon: 'pi pi-sign-out',
                command: () => this.logout()
            }
        ];
    }

    toggleSidebar(): void {
        this.sidebarVisible = !this.sidebarVisible;
    }

    logout(): void {
        this.authService.logout();
    }
}
